import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

// The pieces every plot is built from: the geometry, the rounded data-end,
// and the one hover layer they all share.

export type Box = {
  w: number; h: number
  left: number; right: number; top: number; bottom: number
  iw: number; ih: number
}

export function box(w: number, h: number, left: number, bottom: number, right = 14, top = 12): Box {
  return {
    w, h, left, right, top, bottom,
    iw: Math.max(0, w - left - right),
    ih: Math.max(0, h - top - bottom),
  }
}

/**
 * A rectangle rounded on the DATA end only.
 *
 * A bar is rounded where the value ends and square where it meets the
 * baseline, because the baseline is the zero the eye measures from and a
 * rounded corner there quietly shortens the bar.
 */
export function bar(
  x: number, y: number, w: number, h: number, r: number,
  side: 'top' | 'right',
): string {
  if (w <= 0 || h <= 0) return ''
  const k = Math.max(0, Math.min(r, side === 'top' ? Math.min(w / 2, h) : Math.min(h / 2, w)))
  if (side === 'top') {
    return `M${x},${y + h} L${x},${y + k} Q${x},${y} ${x + k},${y} `
         + `L${x + w - k},${y} Q${x + w},${y} ${x + w},${y + k} L${x + w},${y + h} Z`
  }
  return `M${x},${y} L${x + w - k},${y} Q${x + w},${y} ${x + w},${y + k} `
       + `L${x + w},${y + h - k} Q${x + w},${y + h} ${x + w - k},${y + h} L${x},${y + h} Z`
}

/** A polyline that BREAKS at nulls instead of bridging them. */
export function linePath(points: ({ x: number; y: number } | null)[]): string {
  let d = ''
  let pen = false
  for (const p of points) {
    if (!p) { pen = false; continue }
    d += `${pen ? 'L' : 'M'}${p.x.toFixed(2)},${p.y.toFixed(2)} `
    pen = true
  }
  return d
}

// ------------------------------------------------------------- the tooltip

export type TipRow = { color: string; label: string; value: string }
export type Tip = { x: number; y: number; title: string; rows: TipRow[] } | null

/**
 * The tooltip, rendered into <body>.
 *
 * A chart card has overflow and a border radius, both of which clip an
 * absolutely positioned child; the same reason the combobox list is
 * portalled. Position is fixed and measured against the viewport, and the
 * box flips rather than running off the right edge.
 */
// Layout effects do not exist on the server, and the render check renders
// these components with react-dom/server to audit their geometry. Falling
// back to useEffect there keeps the measurement synchronous in the browser
// -- where it matters, because a tooltip measured a frame late flickers in
// the wrong place -- without warning on every server render.
const useMeasure = typeof window === 'undefined' ? useEffect : useLayoutEffect

export function Tooltip({ tip }: { tip: Tip }) {
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ left: -9999, top: -9999 })

  useMeasure(() => {
    if (!tip || !ref.current) return
    const r = ref.current.getBoundingClientRect()
    const pad = 12
    let left = tip.x + 14
    if (left + r.width > window.innerWidth - pad) left = tip.x - r.width - 14
    let top = tip.y - r.height / 2
    top = Math.max(pad, Math.min(top, window.innerHeight - r.height - pad))
    setPos({ left: Math.max(pad, left), top })
  }, [tip])

  if (!tip) return null
  return createPortal(
    <div ref={ref} className="viz-tip" style={pos} role="status">
      <div className="t">{tip.title}</div>
      {tip.rows.map((r, i) => (
        <div className="r" key={i}>
          <i style={{ background: r.color }} aria-hidden />
          <span>{r.label}</span>
          <b>{r.value}</b>
        </div>
      ))}
    </div>,
    document.body,
  )
}

/** Tracks the pointer and hands back the index it is nearest. */
export function useHover() {
  const [tip, setTip] = useState<Tip>(null)
  const [at, setAt] = useState<number | null>(null)

  const clear = useCallback(() => { setTip(null); setAt(null) }, [])
  const show = useCallback((i: number, t: Omit<NonNullable<Tip>, 'x' | 'y'>, e: { clientX: number; clientY: number }) => {
    setAt(i)
    setTip({ ...t, x: e.clientX, y: e.clientY })
  }, [])

  return { tip, at, show, clear }
}

/**
 * The legend. Present whenever there are two or more series -- identity is
 * never left to colour alone, which is also what makes the light-mode
 * steps that fall below 3:1 contrast legal to use at all.
 *
 * A single series needs none: the chart's title already names it.
 */
export function Legend({ items }: { items: { color: string; label: string }[] }) {
  if (items.length < 2) return null
  return (
    <div className="viz-legend">
      {items.map(it => (
        <span key={it.label}>
          <i style={{ background: it.color }} aria-hidden />
          {it.label}
        </span>
      ))}
    </div>
  )
}
